
import { defineComponent, reactive, onMounted, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGameRepository = RepositoryFactory.get('seasonGame')

import BaseDialog, { Dialog } from '@/components/BaseDialog.vue'
import RequestGrouping from '@/components/seasonGame/RequestGrouping.vue'

import { EntryTeam } from '@/types/entryTeam'
import { GameForm } from '@/types/gameForm'

import { useJquery } from '@/composables/useJquery'
import { useAppearancePlugin } from '@/composables/useAppearancePlugin'

interface TeamBox {
  entry_team_id: number
  entry_team_short_name: string

  priority: number

  startX: number
  startY: number

  line: {
    startX: number
    startY: number
    endX: number
    endY: number
  }
}

interface GameTournamentForm {
  group_id: number
  field_id?: number

  time: number
  tournament_priority: number
  seed: boolean

  home_team_id?: number
  away_team_id?: number

  line: {
    startX: number
    startY: number
    endX: number
    endY: number
  }
}

interface Times {
  games: GameTournamentForm[]
  time: number
}

interface LineButton {
  time: number
  homePriority: number
  awayPriority?: number

  top: number
  left: number

  seed: boolean,
  isShow: boolean

  home_team_id?: number
  away_team_id?: number
}

export default defineComponent({
  inheritAttrs: false,
  components: {
    BaseDialog,
    RequestGrouping
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    const { addJquery } = useJquery()

    onMounted(() => {
      addJquery()

      getTournament()
    })

    const tournament = reactive({
      group: {},
      teams: [],
      times: [],
      isLoading: false
    })

    const existTournament = computed(() => {
      return tournament.times.length > 0
    })

    const existEntryTeams = computed(() => {
      return tournament.teams.length > 1
    })

    const getTournament = async () => {
      try {
        tournament.isLoading = true

        const group_id = route.query.group

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGameRepository.getGameTournament(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          tournament.group = data.group
          tournament.teams = data.teams
          tournament.times = data.times

          if (existTournament.value) {
            showTournament()
          } else {
            if (existEntryTeams.value) setTournament()
          }
        }
      } catch (error) {
        //
      } finally {
        tournament.isLoading = false
      }
    }

    const { addAppearancePlugin } = useAppearancePlugin()

    const showTournament = () => {
      tournament.isLoading = true

      const pluginScript = document.getElementById('ssbee-plugin') as HTMLElement
      if (pluginScript) pluginScript.remove()

      nextTick(() => {
        addAppearancePlugin()
      })

      tournament.isLoading = false
    }

    const canvas: {
      dom?: HTMLCanvasElement
      ctx?: CanvasRenderingContext2D

      width?: number
      height?: number

      canvasTop: number
      canvasLeft: number

      teamBox: {
        width: number
        height: number

        spacing: number
        nameLeft: number

        borderColor: string
        lineWidth: number
      }

      tree: {
        lineColor: string
        lineWidth: number
        lineLength: number
      }

      button: {
        width: number
        height: number

        leftSpacing: number
      }
    } = reactive({
      dom: undefined,
      ctx: undefined,

      width: undefined,
      height: undefined,

      canvasTop: 10,
      canvasLeft: 10,

      teamBox: {
        width: 240,
        height: 34,

        spacing: 40,
        nameLeft: 10,

        borderColor: '#000',
        lineWidth: 1
      },

      tree: {
        lineColor: '#000',
        lineWidth: 2,
        lineLength: 80
      },

      button: {
        width: 40,
        height: 40,

        leftSpacing: 12
      }
    })

    const tournamentTree: {
      teamBoxes: TeamBox[]
      times: Times[]
      games: GameForm[]
    } = reactive({
      teamBoxes: [],
      times: [],
      games: []
    })

    const lineButtons: {
      list: {
        buttons: LineButton[]
        time: number
      }[]
    } = reactive({
      list: []
    })

    const setTournament = () => {
      tournament.isLoading = true

      resetTournament()
      nextTick(() => {
        createCanvas()
        setTimeout(() => {
          createTeamBoxes()
          setFirstTimeButtons()
        }, 100)
      })

      tournament.isLoading = false
    }

    const resetTournament = () => {
      tournamentTree.teamBoxes = []
      tournamentTree.times = []
      tournamentTree.games = []

      lineButtons.list = []
    }

    const createCanvas = () => {
      canvas.dom = document.getElementById('js-canvas-display') as HTMLCanvasElement
      canvas.ctx = canvas.dom.getContext('2d') as CanvasRenderingContext2D

      canvas.width = 1000
      canvas.height = tournament.teams.length * (canvas.teamBox.height + canvas.teamBox.spacing) - canvas.canvasTop

      canvas.ctx.clearRect(0, 0, 800, 800)
    }

    const createTeamBoxes = () => {
      const ctx = canvas.ctx
      if (ctx == undefined) return

      ctx.font = '16px "ヒラギノ角ゴ ProN W3"'
      ctx.textBaseline = 'top'

      tournament.teams.forEach((team: EntryTeam, index: number) => {
        const teamName = `${team.team_short_name} ${team.division != undefined ? team.division : ''}`

        const measure = ctx.measureText(teamName)
        const teamNameHeight = measure.actualBoundingBoxAscent + measure.actualBoundingBoxDescent

        const teamBoxStartX = canvas.canvasLeft
        const teamBoxStartY = canvas.canvasTop + (canvas.teamBox.height + canvas.teamBox.spacing) * index

        const teamNameStartX = canvas.canvasLeft + canvas.teamBox.nameLeft
        const teamNameStartY = canvas.canvasTop + (canvas.teamBox.height / 2 - teamNameHeight / 2) + (canvas.teamBox.height + canvas.teamBox.spacing) * index

        ctx.strokeStyle = canvas.teamBox.borderColor
        ctx.lineWidth = canvas.teamBox.lineWidth

        ctx.beginPath()

        ctx.rect(
          teamBoxStartX,
          teamBoxStartY,
          canvas.teamBox.width,
          canvas.teamBox.height)

        ctx.stroke()

        ctx.fillText(
          teamName,
          teamNameStartX,
          teamNameStartY)

        /* チーム名ボックスに毛を生やす */
        ctx.strokeStyle = canvas.tree.lineColor
        ctx.lineWidth = canvas.tree.lineWidth

        const moveToX = teamBoxStartX + canvas.teamBox.width
        const moveToY = teamBoxStartY + (canvas.teamBox.height / 2 - canvas.tree.lineWidth / 2)

        const startX = moveToX
        const startY = moveToY

        const lineToX = moveToX + canvas.tree.lineLength
        const lineToY = moveToY

        ctx.beginPath()

        ctx.moveTo(moveToX, moveToY)
        ctx.lineTo(lineToX, lineToY)

        ctx.stroke()

        const endX = lineToX
        const endY = lineToY

        tournamentTree.teamBoxes.push({
          entry_team_id: team.entry_team_id,
          entry_team_short_name: team.team_short_name,

          priority: team.priority,

          startX: teamBoxStartX,
          startY: teamBoxStartY,

          line: {
            startX: startX,
            startY: startY,
            endX: endX,
            endY: endY
          }
        })
      })
    }

    const setFirstTimeButtons = () => {
      const firstTimeNumber = 1
      lineButtons.list.push({ time: firstTimeNumber, buttons: [] })
      const firstTimeButtons = lineButtons.list.find(item => item.time === firstTimeNumber)

      const teamBoxes = tournamentTree.teamBoxes

      let buttonTop = 100
      const buttonLeft = canvas.teamBox.width + canvas.button.leftSpacing + canvas.tree.lineLength

   
      for (let i=0; i < teamBoxes.length; i++) {
        const homeTeam = teamBoxes[i]

        buttonTop = homeTeam.line.endY - (canvas.button.height / 2)

        const buttonObj = {
          time: firstTimeNumber,
          homePriority: homeTeam.priority,
          awayPriority: undefined,

          top: buttonTop,
          left: buttonLeft,

          seed: true,
          isShow: true,

          home_team_id: homeTeam.entry_team_id,
          away_team_id: undefined
        }

        firstTimeButtons?.buttons.push(buttonObj)
      }

      for (let i=0; i < teamBoxes.length - 1; i++) {
        const homeTeam = teamBoxes[i]
        const awayTeam = teamBoxes[i + 1]

        buttonTop = (homeTeam.line.endY + awayTeam.line.endY) / 2 - (canvas.button.height / 2)

        const buttonObj = {
          time: firstTimeNumber,
          homePriority: homeTeam.priority,
          awayPriority: awayTeam.priority,

          top: buttonTop,
          left: buttonLeft,

          seed: false,
          isShow: true,

          home_team_id: homeTeam.entry_team_id,
          away_team_id: awayTeam.entry_team_id
        }

        firstTimeButtons?.buttons.push(buttonObj)

        console.log(firstTimeButtons)
      }
    }

    const addLine = (button: LineButton) => {
      const ctx = canvas.ctx
      if (ctx == undefined) return

      ctx.strokeStyle = canvas.tree.lineColor
      ctx.lineWidth = canvas.tree.lineWidth

      let homeMoveToX, homeMoveToY, homeLineToX, homeLineToY
      let awayMoveToX, awayMoveToY, awayLineToX, awayLineToY
      let margeMoveToX, margeMoveToY, margeLineToX, margeLineToY
      let startX, startY, endX, endY

      const currentTimeNumber = button.time

      const firstTime = currentTimeNumber === 1
      const isCurrentHalfTime = !Number.isInteger(currentTimeNumber)

      const prevTimeNumber = isCurrentHalfTime ? currentTimeNumber - 0.5 : currentTimeNumber - 1
      const nextTimeNumber = isCurrentHalfTime ? currentTimeNumber + 0.5 : currentTimeNumber + 1

      let prevTime = tournamentTree.times.find((time) => time.time === prevTimeNumber)
      let homeGame: GameTournamentForm | TeamBox | undefined = prevTime?.games.find((game: GameTournamentForm) => game.tournament_priority === button.homePriority)
      let awayGame: GameTournamentForm | TeamBox | undefined = prevTime?.games.find((game: GameTournamentForm) => game.tournament_priority === button.awayPriority)

      const seed = button.seed === true

    
      if (!isCurrentHalfTime) { // n回戦
        if (firstTime) { // 1回戦
          const homeTeam = tournamentTree.teamBoxes.find((item: TeamBox) => item.priority === button.homePriority)
          const awayTeam = tournamentTree.teamBoxes.find((item: TeamBox) => item.priority === button.awayPriority)

          const group_id = Number(route.query.group)

          if (homeTeam != undefined) {
            homeGame = {
              group_id: group_id,
              field_id: undefined,

              time: currentTimeNumber,
              tournament_priority: homeTeam.priority,
              seed: seed,

              line: {
                startX: homeTeam.line.startX,
                startY: homeTeam.line.startY,
                endX: homeTeam.line.endX,
                endY: homeTeam.line.endY
              }
            }
          }

          if (awayTeam != undefined) {
            awayGame = {
              group_id: group_id,
              field_id: undefined,

              time: currentTimeNumber,
              tournament_priority: awayTeam.priority,
              seed: seed,

              line: {
                startX: awayTeam.line.startX,
                startY: awayTeam.line.startY,
                endX: awayTeam.line.endX,
                endY: awayTeam.line.endY
              }
            }
          }
        }

        if (seed) {
          if (homeGame == undefined) return

          homeMoveToX = homeGame.line.endX
          homeMoveToY = homeGame.line.endY
          homeLineToX = homeMoveToX + canvas.tree.lineLength
          homeLineToY = homeMoveToY

          startX = homeMoveToX
          startY = homeMoveToY

          ctx.beginPath()
          ctx.setLineDash([])

          /* ホームの生え際から毛を生やす */
          ctx.moveTo(homeMoveToX, homeMoveToY)
          ctx.lineTo(homeLineToX, homeLineToY)

          ctx.stroke()

          endX = homeLineToX
          endY = homeLineToY
        } else {
          if (homeGame == undefined || awayGame == undefined) return

          homeMoveToX = homeGame.line.endX
          homeMoveToY = homeGame.line.endY
          homeLineToX = homeMoveToX
          homeLineToY = homeGame.line.endY + (awayGame.line.endY - homeGame.line.endY) / 2

          awayMoveToX = awayGame.line.endX
          awayMoveToY = awayGame.line.endY
          awayLineToX = awayMoveToX
          awayLineToY = homeLineToY

          startX = homeMoveToX
          startY = homeMoveToY

          ctx.beginPath()
          ctx.setLineDash([])

          /* ホームの生え際から毛を生やす */
          ctx.moveTo(homeMoveToX, homeMoveToY)
          ctx.lineTo(homeLineToX, homeLineToY)

          /* アウェイの生え際から毛を生やす */

          ctx.moveTo(awayMoveToX, awayMoveToY)
          ctx.lineTo(awayLineToX, awayLineToY)

          /* ホーム・アウェイの結合点から毛を生やす */
          margeMoveToX = awayLineToX
          margeMoveToY = awayLineToY
          margeLineToX = awayLineToX + canvas.tree.lineLength
          margeLineToY = margeMoveToY

          ctx.moveTo(margeMoveToX, margeMoveToY)
          ctx.lineTo(margeLineToX, margeLineToY)

          ctx.stroke()

          endX = margeLineToX
          endY = margeLineToY
        }
      } else { // n.5回戦
        if (homeGame == undefined || awayGame == undefined) return

        homeMoveToX = homeGame.line.startX
        homeMoveToY = homeGame.line.startY + (homeGame.line.endY - homeGame.line.startY) * 2

        awayMoveToX = awayGame.line.startX
        awayMoveToY = awayGame.line.startY

        homeLineToX = homeMoveToX + canvas.tree.lineLength / 3
        homeLineToY = homeMoveToY

        awayLineToX = awayMoveToX + canvas.tree.lineLength / 3
        awayLineToY = awayMoveToY

        startX = homeMoveToX
        startY = homeMoveToY

        ctx.beginPath()
        ctx.setLineDash([2, 2])

        /* ホームの生え際から横に毛を生やしてから、縦に毛を生やす */
        ctx.moveTo(homeMoveToX, homeMoveToY)
        ctx.lineTo(homeLineToX, homeLineToY)

        homeLineToY = (homeMoveToY + awayMoveToY) / 2
        ctx.lineTo(homeLineToX, homeLineToY)

        /* アウェイの生え際から横に毛を生やしてから、縦に毛を生やす */

        ctx.moveTo(awayMoveToX, awayMoveToY)
        ctx.lineTo(awayLineToX, awayLineToY)

        awayLineToY = homeLineToY
        ctx.lineTo(awayLineToX, awayLineToY)


        /* ホーム・アウェイの結合点から、横に毛を生やす */
        margeLineToX = awayLineToX + canvas.tree.lineLength / 3
        margeLineToY = awayLineToY
        ctx.lineTo(margeLineToX, margeLineToY)
        ctx.stroke()

        endX = margeLineToX
        endY = margeLineToY
      }

      addGame(button, startX, startY, endX, endY)

      if (firstTime) {
        hideUnavailableNuttons(button)
      } else {
        hideUnavailableNuttons(button, homeGame, awayGame)
      }

      if (isCurrentHalfTime) return

      /* 次の回のボタンをセット */
      const currentTimeButtons = lineButtons.list.find(item => item.time === currentTimeNumber)
      // this.currentPriority = 0
      const allButtonsHidden = currentTimeButtons?.buttons.every((button: LineButton) => button.isShow === false)
      if (allButtonsHidden) setNextTimeButtons(nextTimeNumber)
    }

    const addGame = (button: LineButton, startX: number, startY: number, endX: number, endY: number) => {
      const currentTimeNumber = button.time
      const isCurrentHalfTime = !Number.isInteger(currentTimeNumber)
      const prevTimeNumber = isCurrentHalfTime ? currentTimeNumber - 0.5 : currentTimeNumber - 1
      const firstTime = currentTimeNumber === 1
      const seed = button.seed

      const existsCurrenTime = tournamentTree.times.some((time) => time.time === currentTimeNumber)
      if (!existsCurrenTime) tournamentTree.times.push({ time: currentTimeNumber, games: [] })
      const currentTime = tournamentTree.times.find((time) => time.time === currentTimeNumber)

      let gamePriority

      let home_team_id
      let away_team_id

      if (firstTime) {
        const home_team: TeamBox | undefined = tournament.teams.find((team: TeamBox) => team.entry_team_id === button.home_team_id)
        gamePriority = home_team!.priority

        home_team_id = button.home_team_id
        away_team_id = button.away_team_id
      } else {
        let prevTime = tournamentTree.times.find((time) => time.time === prevTimeNumber)
        const homeGame = prevTime?.games.find((game: GameTournamentForm) => game.tournament_priority === button.homePriority)

        gamePriority = homeGame!.tournament_priority

        home_team_id = undefined
        away_team_id = undefined
      }

      const group_id = parseInt(route.query.group as string)

      const game = {
        group_id: group_id,
        field_id: undefined,

        time: currentTimeNumber,
        tournament_priority: gamePriority,
        seed: seed,

        home_team_id: home_team_id,
        away_team_id: away_team_id,

        line: {
          startX: startX,
          startY: startY,
          endX: endX,
          endY: endY
        }
      }

      currentTime?.games.push(game)
    }

    const hideUnavailableNuttons = (button: LineButton, homeGame?: GameTournamentForm, awayGame?: GameTournamentForm) => {
      const currentTimeNumber = button.time
      const firstTime = currentTimeNumber === 1
      const isCurrentHalfTime = !Number.isInteger(currentTimeNumber)
      const seed = button.seed

      const currentLineButtons = lineButtons.list.find((item) => item.time === currentTimeNumber)

      let homePriority: number | undefined
      let awayPriority: number | undefined

      if (firstTime) {
        homePriority = button.homePriority
        awayPriority = button.awayPriority
      } else {
        homePriority = homeGame?.tournament_priority
        awayPriority = awayGame?.tournament_priority
      }

      if (isCurrentHalfTime) {
        button.isShow = false
      } else {
        let homeTeamButtons
        let awayTeamButtons

        if (seed) {
          homeTeamButtons = currentLineButtons?.buttons.filter(button => button.homePriority === homePriority)
          awayTeamButtons = currentLineButtons?.buttons.filter(button => button.homePriority === awayPriority)
        } else {
          homeTeamButtons = currentLineButtons?.buttons.filter(button => button.homePriority === homePriority || button.awayPriority === homePriority)
          awayTeamButtons = currentLineButtons?.buttons.filter(button => button.homePriority === awayPriority || button.awayPriority === awayPriority)
        }

        if (homeTeamButtons) homeTeamButtons.forEach(button => button.isShow = false)
        if (awayTeamButtons) awayTeamButtons.forEach(button => button.isShow = false)
      }
    }

    const setNextTimeButtons = (nextTimeNumber: number) => {
      const currentTimeNumber = nextTimeNumber - 1
      const nextHalfTimeNumber = nextTimeNumber - 0.5

      const currentTime = tournamentTree.times.find((time: Times)=> time.time === currentTimeNumber)

      if (currentTime?.games.length === 1 || currentTime == undefined) return

      currentTime?.games.sort((a, b) => {
        if (a.tournament_priority < b.tournament_priority) return -1
        if (a.tournament_priority > b.tournament_priority) return 1
        return 0
      })

      lineButtons.list.push({ time: nextHalfTimeNumber, buttons: [] })
      lineButtons.list.push({ time: nextTimeNumber, buttons: [] })

      const nextHalfTimeButtons = lineButtons.list.find(item => item.time === nextHalfTimeNumber)
      const nextTimeButtons = lineButtons.list.find(item => item.time === nextTimeNumber)

      for (let i=0; i < currentTime.games.length; i++) {
        const homeGame = currentTime.games[i]

        const buttonTop = homeGame.line.endY - (canvas.button.height / 2)
        const buttonLeft = canvas.teamBox.width + canvas.button.leftSpacing + (canvas.tree.lineLength * nextTimeNumber)

        const buttonObj = {
          time: nextTimeNumber,
          homePriority: homeGame.tournament_priority,
          awayPriority: undefined,

          top: buttonTop,
          left: buttonLeft,

          seed: true,
          isShow: true,

          home_team_id: undefined,
          away_team_id: undefined
        }

        nextTimeButtons?.buttons.push(buttonObj)
      }

      for (let i=0; i < currentTime.games.length - 1; i++) {
        const homeGame = currentTime.games[i]
        const awayGame = currentTime.games[i + 1]

        const buttonTop = (homeGame.line.endY + awayGame.line.endY) / 2 - (canvas.button.height / 2)
        const buttonLeft = canvas.teamBox.width + canvas.button.leftSpacing + (canvas.tree.lineLength * nextTimeNumber)

        const halfButtonLeft = canvas.teamBox.width + canvas.button.leftSpacing + (canvas.tree.lineLength * currentTimeNumber)

        /* current+1回戦 */
        const nextButtonObj = {
          time: nextTimeNumber,
          homePriority: homeGame.tournament_priority,
          awayPriority: awayGame.tournament_priority,

          top: buttonTop,
          left: buttonLeft,

          seed: false,
          isShow: true,

          home_team_id: undefined,
          away_team_id: undefined
        }

        nextTimeButtons?.buttons.push(nextButtonObj)

        /* current+1.5回戦 */
        if (currentTime.games.length < 1) return

        if (!homeGame.seed && !awayGame.seed) {
          const nextHalfButtonObj = {
            time: nextHalfTimeNumber,
            homePriority: homeGame.tournament_priority,
            awayPriority: awayGame.tournament_priority,

            top: buttonTop,
            left: halfButtonLeft,

            seed: false,
            isShow: true,

            home_team_id: undefined,
            away_team_id: undefined
          }

          nextHalfTimeButtons?.buttons.push(nextHalfButtonObj)
        }
      }
    }

    const onClickSave = () => {
      sortTournament()
      postTournament()
    }

    const sortTournament = () => {
      let gameArray: any = []

      tournamentTree.times.sort((a, b) => {
        if (a.time < b.time) return -1
        if (a.time > b.time) return 1
        return 0
      })

      let priorityCounter = 1

      tournamentTree.times.forEach(item => {
        const games = item.games
        games.sort((a, b) => {
          if (a.tournament_priority < b.tournament_priority) return -1
          if (a.tournament_priority > b.tournament_priority) return 1
          return 0
        })

        games.forEach((game: any) => {
          game.priority = priorityCounter++

          delete game.line
        })

        gameArray.push(games)
      })

      tournamentTree.games = gameArray.flat()
    }

    const postTournament = async () => {
      try {
        const { season_id } = route.params
        const custom_id = route.query.custom

        const dataObj = {
          custome_id: custom_id,
          list: tournamentTree.games
        }

        const res = await SeasonGameRepository.postGames(season_id, dataObj)

        if (res.status === 200) {
          getTournament()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'トーナメントを保存しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const tournamentDialogDelete = reactive<Dialog>({
      title: 'トーナメントを作り直しますか？',
      message: '現在このトーナメントに登録されている試合の情報は全て削除されます。',
      confirmText: '作り直す',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openTournamentDialogDelete = () => {
      tournamentDialogDelete.isOpen = true
    }

    const closeTournamentDialogDelete = () => {
      tournamentDialogDelete.isOpen = false
    }

    const deleteGroupGames = async () => {
      try {
        const { season_id } = route.params
        const group_id = route.query.group

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGameRepository.deleteGroupGames(season_id, dataObj)

        if (res.status === 200) {
          tournament.times = []
          closeTournamentDialogDelete()
          getTournament()
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'トーナメントを削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    return {
      tournament,
      existTournament,
      existEntryTeams,
      getTournament,
      showTournament,

      tournamentTree,
      lineButtons,
      canvas,
      setTournament,

      createCanvas,
      addLine,

      setNextTimeButtons,

      resetTournament,

      onClickSave,
      sortTournament,
      postTournament,

      tournamentDialogDelete,
      openTournamentDialogDelete,
      closeTournamentDialogDelete,
      deleteGroupGames
    }
  }
})
